import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AreaTrendChart, type AreaTrendFold } from '../../shared/charts/AreaTrendChart';
import { SegmentedControl } from '../../shared/control/SegmentedControl';
import type { MoneyLanguage } from '../../shared/format/money';
import type { TimeLanguage } from '../../shared/format/time';
import { SectionCard } from '../../shared/surface/SectionCard';
import { compactFormat, countFormat, toTrendSeries, type TrendMetric } from './trendSeries';
import type { DashboardTrends } from './types';

export function TrendChart({ trends }: { readonly trends: DashboardTrends }) {
  const { i18n, t } = useTranslation();
  const language: MoneyLanguage & TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [metric, setMetric] = useState<TrendMetric>('playAmount');
  const { categories, series } = toTrendSeries(trends, metric, language);
  // One series per currency. Counts are additive across currencies, so a summed "Other" line is
  // meaningful; amounts are not, so those cap instead — see AreaTrendFold.
  const counts = countFormat(language);
  const fold: AreaTrendFold =
    metric === 'transactionCount'
      ? { formatLabel: (value) => counts.format(value), kind: 'sum' }
      : { kind: 'cap' };
  // Axis ticks are shared by every series, so they cannot carry a currency symbol without implying that
  // one currency's scale applies to all of them. Counts get grouped digits; amounts get a compact
  // magnitude, and the exact figure with its currency lives in the tooltip and the accessible table.
  const formatValue =
    metric === 'transactionCount'
      ? (value: number) => counts.format(value)
      : (value: number) => compactFormat(language).format(value);

  return (
    <SectionCard
      action={
        <SegmentedControl
          ariaLabel={t('dashboard.charts.metric')}
          onChange={setMetric}
          options={[
            { label: t('dashboard.charts.playAmount'), value: 'playAmount' },
            { label: t('dashboard.charts.transactionCount'), value: 'transactionCount' },
          ]}
          value={metric}
        />
      }
      title={t('dashboard.trends')}
    >
      {categories.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t('dashboard.empty')}
        </Typography>
      ) : (
        <>
          <AreaTrendChart
            // The name follows the selected metric: a chart announced as the actual-play chart while
            // showing transaction counts misdescribes itself.
            ariaLabel={
              metric === 'transactionCount'
                ? t('dashboard.charts.trendLabelTransactionCount')
                : t('dashboard.charts.trendLabelPlayAmount')
            }
            categories={categories}
            fold={fold}
            formatValue={formatValue}
            // The rows are periods, not metrics. 'Metric' names the segmented control beside this chart,
            // so reusing it here named two different things in one card.
            rowHeader={t('dashboard.charts.period')}
            series={series}
            tableCaption={t('dashboard.charts.trendTable')}
          />
          {/*
            Closed by default. Before this redesign the dashboard printed this list outright, and a test
            still asserted it was visible; an expanded raw data dump under every chart is exactly the
            clutter this work set out to remove. Collapsing it costs nothing in reach: AreaTrendChart
            always renders a visually-hidden table carrying every value, which did not exist before, so
            assistive technology is strictly better served than it was when the list was the only path.
          */}
          <Box component="details" sx={{ mt: 1.5 }}>
            <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main' }}>
              <Typography component="span" variant="body2">
                {t('dashboard.charts.showData')}
              </Typography>
            </Box>
            <Box component="ul" aria-label={t('dashboard.trendSummary')} sx={{ mb: 0, pl: 2.5 }}>
              {series.flatMap((entry) =>
                entry.labels.map((label, index) => (
                  <Typography
                    component="li"
                    key={`${entry.name}-${categories[index] ?? String(index)}`}
                    variant="body2"
                  >
                    {categories[index]}: {label}
                  </Typography>
                )),
              )}
            </Box>
          </Box>
        </>
      )}
    </SectionCard>
  );
}
